<template>
  <div class="notebook-page">
    <div class="page-head">
      <div>
        <div class="pill">笔记本</div>
        <h2>题目笔记本</h2>
        <p class="sub">管理搜题笔记本，支持新建、记录、删除、导出和题目批量管理</p>
      </div>
      <div class="head-actions">
        <div class="manage-toggle" v-if="canManageAll">
          <span>管理模式</span>
          <a-switch v-model:checked="manageMode" size="small" @change="handleManageToggle" />
        </div>
        <div class="record-status" v-if="activeNotebookId">
          <a-tag color="green">记录中：{{ activeNotebookName }}</a-tag>
          <a-button type="default" size="small" @click="stopRecording(activeNotebookId)">
            停止记录
          </a-button>
        </div>
      </div>
    </div>

    <a-card class="glass-card" :bordered="false">
      <div class="create-row">
        <a-form layout="inline" @submit.prevent>
          <a-form-item label="新建笔记本">
            <a-input v-model:value="createForm.name" placeholder="输入笔记本名" allow-clear style="width: 220px" />
          </a-form-item>
          <a-form-item label="描述">
            <a-input v-model:value="createForm.desc" placeholder="可选" allow-clear style="width: 260px" />
          </a-form-item>
          <a-form-item>
            <a-button type="primary" :loading="createLoading" @click="handleCreateNotebook">添加</a-button>
          </a-form-item>
        </a-form>
        <div class="hint">同一时间只记录一个笔记本，可随时切换</div>
      </div>
    </a-card>

    <a-card class="glass-card" :loading="loading" bordered>
      <div class="table-head">
        <div class="card-title">笔记本列表</div>
        <a-button type="default" size="small" @click="fetchNotebooks">刷新</a-button>
      </div>
      <a-table
        :columns="columns"
        :data-source="notebookList"
        row-key="id"
        :pagination="false"
        :loading="loading"
        class="simple-table"
      >
        <template #name="{ record }">
          <div class="name-cell">
            <div class="name">{{ record.name }}</div>
            <div class="desc" v-if="record.description">{{ record.description }}</div>
          </div>
        </template>
        <template #status="{ record }">
          <a-tag :color="record.isRecording ? 'green' : 'default'">
            {{ record.isRecording ? '记录中' : '未记录' }}
          </a-tag>
        </template>
        <template #count="{ record }">
          <span>{{ record.questionCount ?? 0 }}</span>
        </template>
        <template #updatedAt="{ record }">
          {{ formatDate(record.updatedAt || record.createdAt) }}
        </template>
        <template #actions="{ record }">
          <div class="action-group">
            <a-button
              size="small"
              type="primary"
              ghost
              @click="toggleRecording(record)"
            >
              {{ isActiveNotebook(record) ? '停止' : '记录' }}
            </a-button>
            <a-button size="small" @click="openNotebook(record)">编辑</a-button>
            <a-button size="small" @click="handleExport(record)" :loading="exportingId === record.id">导出</a-button>
            <a-popconfirm title="确认删除该笔记本？" @confirm="handleDelete(record)">
              <a-button size="small" danger>删除</a-button>
            </a-popconfirm>
          </div>
        </template>
      </a-table>
      <div v-if="!notebookList.length && !loading" class="empty">暂无笔记本，请添加</div>
    </a-card>

    <a-drawer
      :visible="drawerVisible"
      :title="currentNotebook?.name ? `编辑：${currentNotebook.name}` : '笔记本详情'"
      placement="right"
      :width="760"
      @close="closeDrawer"
    >
      <div class="drawer-head">
        <div>
          <div class="drawer-title">{{ currentNotebook?.name }}</div>
          <div class="drawer-sub">
            <span>题目数：{{ currentNotebook?.questionCount ?? 0 }}</span>
            <span v-if="currentNotebook?.description">描述：{{ currentNotebook.description }}</span>
          </div>
        </div>
        <div class="drawer-actions">
          <a-button type="default" @click="fetchNotebookItems">刷新</a-button>
          <a-button
            type="primary"
            danger
            :disabled="!selectedItemKeys.length"
            :loading="itemDeleting"
            @click="batchDeleteItems"
          >删除选中</a-button>
        </div>
      </div>
      <a-table
        :row-selection="rowSelection"
        :columns="itemColumns"
        :data-source="itemList"
        row-key="id"
        :loading="itemLoading"
        :pagination="false"
        size="small"
      >
        <template #questionId="{ record }">
          {{ record.questionId || record.id }}
        </template>
        <template #question="{ record }">
          <div class="question-text" :title="record.question">
            {{ record.question || '-' }}
          </div>
        </template>
        <template #itemActions="{ record }">
          <a-popconfirm title="删除该题目？" @confirm="deleteItem(record)">
            <a-button size="small" danger>删除</a-button>
          </a-popconfirm>
        </template>
        <template #createdAt="{ record }">
          {{ formatDate(record.createdAt) }}
        </template>
      </a-table>
      <div class="table-foot">
        <a-pagination
          size="small"
          :current="itemPage.current"
          :page-size="itemPage.size"
          :total="itemPage.total"
          :show-size-changer="true"
          :page-size-options="['10','20','50']"
          @change="onItemPageChange"
          @showSizeChange="onItemSizeChange"
        />
      </div>
    </a-drawer>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import {
  getNotebooks,
  createNotebook,
  deleteNotebook,
  startNotebook,
  stopNotebook,
  exportNotebook,
  getNotebookItems,
  deleteNotebookItems
} from '@/api/TikuComponentApi'
import { message } from 'ant-design-vue'
import { hasGlobalAccess } from '@/utils/permissions'
import { asArrayResult, asPageResult } from '@/utils/result'

const ACTIVE_ID_KEY = 'adapterActiveNotebookId'
const ACTIVE_NAME_KEY = 'adapterActiveNotebookName'

export default defineComponent({
  name: 'NotebookPage',
  setup() {
    const notebookList = ref([])
    const loading = ref(false)
    const createLoading = ref(false)
    const exportingId = ref(null)
    const createForm = ref({ name: '', desc: '' })
    const manageMode = ref(false)
    const activeNotebookId = ref(localStorage.getItem(ACTIVE_ID_KEY) || '')
    const activeNotebookName = ref(localStorage.getItem(ACTIVE_NAME_KEY) || '')
    const canManageAll = hasGlobalAccess()

    const drawerVisible = ref(false)
    const currentNotebook = ref(null)
    const itemList = ref([])
    const itemLoading = ref(false)
    const itemDeleting = ref(false)
    const selectedItemKeys = ref([])
    const itemPage = ref({ current: 1, size: 10, total: 0 })

    const columns = [
      { title: '名称', dataIndex: 'name', key: 'name', slots: { customRender: 'name' } },
      { title: '状态', key: 'status', width: 120, slots: { customRender: 'status' } },
      { title: '题目数', dataIndex: 'questionCount', key: 'count', width: 120, slots: { customRender: 'count' } },
      { title: '更新时间', dataIndex: 'updatedAt', key: 'updatedAt', width: 180, slots: { customRender: 'updatedAt' } },
      { title: '操作', key: 'actions', width: 260, fixed: 'right', slots: { customRender: 'actions' } }
    ]

    const itemColumns = [
      { title: '题目ID', dataIndex: 'questionId', key: 'questionId', width: 120, slots: { customRender: 'questionId' } },
      { title: '题目内容', dataIndex: 'question', key: 'question', ellipsis: true, slots: { customRender: 'question' } },
      { title: '添加时间', dataIndex: 'createdAt', key: 'createdAt', width: 170, slots: { customRender: 'createdAt' } },
      { title: '操作', key: 'itemActions', width: 90, slots: { customRender: 'itemActions' } }
    ]

    const rowSelection = computed(() => ({
      selectedRowKeys: selectedItemKeys.value,
      onChange: keys => { selectedItemKeys.value = keys }
    }))

    const persistActiveNotebook = (id, name) => {
      const idStr = id ? String(id) : ''
      activeNotebookId.value = idStr
      activeNotebookName.value = name || ''
      if (idStr) {
        localStorage.setItem(ACTIVE_ID_KEY, idStr)
        localStorage.setItem(ACTIVE_NAME_KEY, name || '')
      } else {
        localStorage.removeItem(ACTIVE_ID_KEY)
        localStorage.removeItem(ACTIVE_NAME_KEY)
      }
    }

    const normalizeNotebook = (item) => ({
      id: item.id || item.notebookId,
      name: item.name || item.title || '未命名',
      description: item.description || item.desc || '',
      questionCount: item.questionCount ?? item.itemCount ?? item.count ?? 0,
      isRecording: item.isRecording || item.recording || String(activeNotebookId.value) === String(item.id || item.notebookId),
      updatedAt: item.updatedAt || item.updateTime || item.createdAt,
      createdAt: item.createdAt || item.createTime
    })

    const isActiveNotebook = (record) => String(record.id) === String(activeNotebookId.value)

    const fetchNotebooks = async () => {
      loading.value = true
      try {
        const params = {}
        if (canManageAll && manageMode.value) {
          params.scope = 'all'
        }
        const res = await getNotebooks(params)
        const list = asArrayResult(res?.data)
        notebookList.value = (list || []).map(normalizeNotebook)
      } finally {
        loading.value = false
      }
    }

    const handleManageToggle = () => {
      fetchNotebooks()
    }

    const handleCreateNotebook = async () => {
      if (!createForm.value.name?.trim()) {
        message.warning('请输入笔记本名')
        return
      }
      createLoading.value = true
      try {
        const payload = { name: createForm.value.name.trim(), description: createForm.value.desc?.trim() }
        const res = await createNotebook(payload)
        if (res?.status === 200) {
          message.success('已新建笔记本')
          createForm.value = { name: '', desc: '' }
          await fetchNotebooks()
        } else {
          message.error(res?.message || '新建失败')
        }
      } finally {
        createLoading.value = false
      }
    }

    const stopRecording = async (id) => {
      if (!id) return
      const res = await stopNotebook(id)
      if (res?.status === 200) {
        message.success('已停止记录')
      }
      persistActiveNotebook('', '')
      fetchNotebooks()
    }

    const toggleRecording = async (record) => {
      if (isActiveNotebook(record)) {
        await stopRecording(record.id)
        return
      }
      if (activeNotebookId.value && activeNotebookId.value !== record.id) {
        await stopRecording(activeNotebookId.value)
      }
      const res = await startNotebook(record.id)
      if (res?.status === 200) {
        message.success('已开始记录')
        persistActiveNotebook(record.id, record.name)
        fetchNotebooks()
      } else {
        message.error(res?.message || '无法开始记录')
      }
    }

    const handleDelete = async (record) => {
      const res = await deleteNotebook(record.id)
      if (res?.status === 200) {
        message.success('已删除')
        if (activeNotebookId.value === record.id) {
          persistActiveNotebook('', '')
        }
        fetchNotebooks()
      } else {
        message.error(res?.message || '删除失败')
      }
    }

    const handleExport = async (record) => {
      exportingId.value = record.id
      try {
        const res = await exportNotebook(record.id, { format: 'word' })
        if (res?.status === 200 && res?.data) {
          const blob = new Blob([res.data], { type: 'application/msword' })
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `${record.name || 'notebook'}.doc`
          a.click()
          URL.revokeObjectURL(url)
          message.success('已导出')
        } else {
          message.error(res?.message || '导出失败')
        }
      } finally {
        exportingId.value = null
      }
    }

    const openNotebook = (record) => {
      currentNotebook.value = record
      drawerVisible.value = true
      itemPage.value = { current: 1, size: 10, total: 0 }
      selectedItemKeys.value = []
      fetchNotebookItems()
    }

    const closeDrawer = () => {
      drawerVisible.value = false
      currentNotebook.value = null
      itemList.value = []
      selectedItemKeys.value = []
    }

    const fetchNotebookItems = async () => {
      if (!currentNotebook.value?.id) return
      itemLoading.value = true
      try {
        const params = { page: itemPage.value.current, pageSize: itemPage.value.size }
        const res = await getNotebookItems(currentNotebook.value.id, params)
        const pageResult = asPageResult(res?.data, itemPage.value.size)
        itemList.value = pageResult.data.map(item => ({
          id: item.id || item.itemId || item.questionId,
          questionId: item.questionId || item.id,
          question: item.question || item.questionTitle,
          createdAt: item.createdAt || item.createTime
        }))
        itemPage.value.total = pageResult.total
      } finally {
        itemLoading.value = false
      }
    }

    const deleteItem = async (record) => {
      if (!currentNotebook.value?.id) return
      itemDeleting.value = true
      try {
        const res = await deleteNotebookItems(currentNotebook.value.id, { ids: [record.id || record.questionId] })
        if (res?.status === 200) {
          message.success('已删除')
          fetchNotebookItems()
          fetchNotebooks()
        } else {
          message.error(res?.message || '删除失败')
        }
      } finally {
        itemDeleting.value = false
      }
    }

    const batchDeleteItems = async () => {
      if (!selectedItemKeys.value.length || !currentNotebook.value?.id) return
      itemDeleting.value = true
      try {
        const res = await deleteNotebookItems(currentNotebook.value.id, { ids: selectedItemKeys.value })
        if (res?.status === 200) {
          message.success('已删除选中题目')
          selectedItemKeys.value = []
          fetchNotebookItems()
          fetchNotebooks()
        } else {
          message.error(res?.message || '批量删除失败')
        }
      } finally {
        itemDeleting.value = false
      }
    }

    const onItemPageChange = (page) => {
      itemPage.value.current = page
      fetchNotebookItems()
    }

    const onItemSizeChange = (current, size) => {
      itemPage.value.size = size
      itemPage.value.current = 1
      fetchNotebookItems()
    }

    const formatDate = (val) => {
      if (!val) return '-'
      const d = new Date(val)
      if (isNaN(d.getTime())) return '-'
      const Y = d.getFullYear()
      const M = String(d.getMonth() + 1).padStart(2, '0')
      const D = String(d.getDate()).padStart(2, '0')
      const h = String(d.getHours()).padStart(2, '0')
      const m = String(d.getMinutes()).padStart(2, '0')
      return `${Y}-${M}-${D} ${h}:${m}`
    }

    onMounted(() => {
      fetchNotebooks()
    })

    return {
      notebookList,
      loading,
      columns,
      itemColumns,
      createForm,
      createLoading,
      exportingId,
      activeNotebookId,
      activeNotebookName,
      drawerVisible,
      currentNotebook,
      itemList,
      itemPage,
      itemLoading,
      itemDeleting,
      selectedItemKeys,
      rowSelection,
      isActiveNotebook,
      canManageAll,
      manageMode,
      formatDate,
      fetchNotebooks,
      handleManageToggle,
      handleCreateNotebook,
      toggleRecording,
      stopRecording,
      handleDelete,
      handleExport,
      openNotebook,
      closeDrawer,
      fetchNotebookItems,
      deleteItem,
      batchDeleteItems,
      onItemPageChange,
      onItemSizeChange
    }
  }
})
</script>

<style scoped>
.notebook-page {
  min-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #e6f7ff;
  color: #1890ff;
  border-radius: 999px;
  font-size: 12px;
  margin-bottom: 6px;
}

h2 {
  margin: 0;
}

.sub {
  margin: 4px 0 0;
  color: #8c8c8c;
}

.head-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.manage-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #555;
}

.record-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.glass-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(24, 144, 255, 0.06);
}

.create-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}

.hint {
  color: #8c8c8c;
  font-size: 12px;
}

.table-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
}

.name-cell .name {
  font-weight: 600;
  color: #1f1f1f;
}

.name-cell .desc {
  color: #8c8c8c;
  font-size: 12px;
}

.action-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.empty {
  text-align: center;
  color: #aaa;
  padding: 16px 0;
}

.drawer-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.drawer-title {
  font-size: 18px;
  font-weight: 600;
}

.drawer-sub {
  display: flex;
  gap: 12px;
  color: #8c8c8c;
  margin-top: 4px;
}

.drawer-actions {
  display: flex;
  gap: 8px;
}

.question-text {
  max-width: 420px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table-foot {
  margin-top: 12px;
  text-align: right;
}
</style>
